import { query } from '@confluence/query-preloader-tools';
import { getApolloClient } from '@confluence/graphql';
import { getLogger } from '@confluence/logger';

import { CommentType } from './__types__/ActiveCommentsQuery';
import { ActiveCommentsQuery } from './ActiveCommentsQuery.graphql';
import {
	buildConsistentActiveCommentsQueryPreloadVariables,
	buildConsistentActiveCommentsQueryVariables,
} from './helper/buildConsistentQueryVariablesHelper';

const logger = getLogger('preloadFooterComments');

export function preloadFooterComments(contentId: string, isArchived?: boolean) {
	// Clear the cache BEFORE load. This query only every gets called with cache-only, so this prevents stale cache errors
	const client = getApolloClient();
	try {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (!process.env.REACT_SSR) {
			// On the server, we can sometimes fail to update the cache either at all, or at least in the expected order, which causes this query to look like it's forever loading
			client.cache.writeQuery({
				query: ActiveCommentsQuery,
				variables: buildConsistentActiveCommentsQueryPreloadVariables(contentId, isArchived),
				data: {
					comments: {
						nodes: [],
						totalCount: -1, // We can't do cache.evict based on apollo version, so use -1 as a flag
						__typename: 'PaginatedCommentList',
					},
				},
			});
		}
	} catch (error) {
		// Do nothing, this is expected if the query is not in the cache
		logger.error`Error clearing preload cache for footer comments for contentId: ${contentId}, error: ${error}`;
	}

	if (contentId) {
		return query({
			query: ActiveCommentsQuery,
			variables: buildConsistentActiveCommentsQueryPreloadVariables(contentId, isArchived),
			fetchPolicy: 'network-only',
		}).then((response: any) => {
			try {
				if (response.data && response.data?.totalCount === 0) {
					// Only write to cache if there are no comments. This isn't strictly necessary - but simplifies our cache updates later on
					// Since we won't call the full query in this case
					client.cache.writeQuery({
						query: ActiveCommentsQuery,
						variables: buildConsistentActiveCommentsQueryVariables({
							pageId: contentId,
							type: CommentType.FOOTER,
							isArchived,
						}),
						data: response.data,
					});
				}
			} catch (error) {
				logger.error`Error writing to full cache for footer comments for contentId: ${contentId}, error: ${error}`;
			}
		});
	} else {
		return Promise.resolve({});
	}
}
